import { startOfToday, subDays, format } from 'date-fns';
import { AuthenticatedHttp } from '../../http/axios';
import { parsePaginationResponse } from '../../utils/api';
import { downloadFile } from '../../utils/files';
import { ALL_CURRENCIES_FILTER_VALUE } from '../../utils/currency';
import { CACHE_TTL } from '../constants';
import { EMAIL_FAILED_TAB } from './emailFailedTab';

const ordersCache = new Map();

function getDateRangeFromPeriod(period) {
  const today = startOfToday();

  switch (period) {
    case 'today':
      return {
        startDate: format(today, 'dd-MM-yyyy'),
        endDate: format(today, 'dd-MM-yyyy'),
      };
    case '7days':
      return {
        startDate: format(subDays(today, 6), 'dd-MM-yyyy'),
        endDate: format(today, 'dd-MM-yyyy'),
      };
    case '30days':
      return {
        startDate: format(subDays(today, 29), 'dd-MM-yyyy'),
        endDate: format(today, 'dd-MM-yyyy'),
      };
    default:
      return {};
  }
}

function normalizeArray(arr) {
  if (!Array.isArray(arr)) return [];
  return arr;
}

function buildOrdersParams({ page, search, status, filter, currency }) {
  const f = filter || {};
  const period = f.period || 'all_time';

  const isEmailFailedTab = status === EMAIL_FAILED_TAB;
  const tabStatus = isEmailFailedTab ? 'paid' : status;
  const statusParam =
    Array.isArray(f.status) && f.status.length > 0 ? f.status : tabStatus;

  const params = {
    page,
    search,
    ...(currency && currency !== ALL_CURRENCIES_FILTER_VALUE && { currency }),
    ...(statusParam ? { status: statusParam } : {}),
    ...(isEmailFailedTab && { email_delivery: 'failed' }),
    ...(period !== 'all_time' && getDateRangeFromPeriod(period)),
    ...(f.startDate && period !== 'all_time' && {
      startDate: format(f.startDate, 'dd-MM-yyyy'),
    }),
    ...(f.endDate && period !== 'all_time' && {
      endDate: format(f.endDate, 'dd-MM-yyyy'),
    }),
    ...(f.coproductionType && f.coproductionType !== 'all' && {
      coproductionType: f.coproductionType,
    }),
    ...(normalizeArray(f.products).length > 0 && {
      products: normalizeArray(f.products),
    }),
    ...(normalizeArray(f.offers).length > 0 && {
      offers: normalizeArray(f.offers),
    }),
    ...(normalizeArray(f.affiliates).length > 0 && {
      affiliate: normalizeArray(f.affiliates),
    }),
    ...(normalizeArray(f.countries).length > 0 && {
      countries: normalizeArray(f.countries),
    }),
    ...(normalizeArray(f.paymentMethods).length > 0 && {
      paymentMethods: normalizeArray(f.paymentMethods),
    }),
    ...(normalizeArray(f.subscription_type).length > 0 && {
      subscription_type: normalizeArray(f.subscription_type),
    }),
    ...(f.offerType && f.offerType !== 'all' && {
      offerType: f.offerType,
    }),
    ...(!!f.utm_source && { utm_source: f.utm_source }),
    ...(!!f.utm_medium && { utm_medium: f.utm_medium }),
    ...(!!f.utm_campaign && { utm_campaign: f.utm_campaign }),
    ...(!!f.utm_content && { utm_content: f.utm_content }),
    ...(!!f.utm_term && { utm_term: f.utm_term }),
    ...(!!f.sck && { sck: f.sck }),
    ...(f.utm_exact_match && { utmMatchMode: 'exact' }),
  };

  return params;
}

function makeCacheKey(params) {
  return JSON.stringify(params);
}

export const clearOrdersCache = () => {
  ordersCache.clear();
};

function fetchOrdersPage(url, params, key) {
  const now = Date.now();

  const cached = ordersCache.get(key);

  if (cached) {
    if (cached.data && now - cached.ts < CACHE_TTL) return cached.data;
    if (cached.promise) return cached.promise;
  }

  const promise = AuthenticatedHttp.get(url, { params })
    .then(parsePaginationResponse)
    .then((data) => {
      ordersCache.set(key, { data, ts: Date.now() });
      return data;
    })
    .catch((e) => {
      ordersCache.delete(key);
      throw e;
    });

  ordersCache.set(key, { promise, ts: now });

  return promise;
}

export const getOrders = async ({ page, search, status, filter, currency }) => {
  const params = buildOrdersParams({ page, search, status, filter, currency });
  return fetchOrdersPage('/orders/', params, makeCacheKey(params));
};

// Mesmos parâmetros da /orders/, com os order bumps dentro da venda principal (CKTPL-1363). A
// chave de cache é outra porque, para os mesmos filtros, a página e o `count` são diferentes.
export const getGroupedOrders = async ({ page, search, status, filter, currency }) => {
  const params = buildOrdersParams({ page, search, status, filter, currency });
  return fetchOrdersPage('/orders/grouped/', params, `grouped:${makeCacheKey(params)}`);
};

// O back devolve só os países em que o usuário tem venda em USD (CKTPC-1305); a ordem pelo
// nome fica aqui do lado, que é quem traduz o código.
export const getOrderCountriesService = async () => {
  const response = await AuthenticatedHttp.get('/orders/countries/');
  return response.data?.countries || [];
};

export const refundOrderService = async ({ data }) =>
  AuthenticatedHttp.post(`/order/${data.id}/refund/`).then(({ data: order }) => order);

export const exportReport = async (values) => {
  const { extension, page, search, status, filter, currency } = values;

  // Reutiliza o mesmo builder usado em getOrders para garantir paridade
  // entre os filtros aplicados na tela e os enviados na exportação.
  // Antes, exportReport montava sua própria querystring e deixava de fora
  // utm_source/utm_medium/utm_campaign/utm_content/utm_term, sck,
  // subscription_type e a expansão de períodos nomeados (today/7days/30days),
  // fazendo o relatório por e-mail ignorar esses filtros (CKTPL-698).
  const queryParams = {
    ...buildOrdersParams({ page, search, status, filter, currency }),
    ...(Array.isArray(filter?.shipping_status) && filter?.shipping_status?.length > 0 && {
      shipping_status: filter.shipping_status,
    }),
  };

  const res = await AuthenticatedHttp.post(
    '/reports/orders/export/email/',
    { format: extension },
    { params: queryParams, responseType: 'arraybuffer', validateStatus: (s) => s >= 200 && s < 400 }
  );

  const contentType = res.headers?.['content-type'] || '';
  if (contentType.includes('application/json')) {
    const json = JSON.parse(new TextDecoder().decode(res.data));

    if (json?.job_id) {
      return { emailed: true };
    }

    if (json?.redirect_to) {
      let url = json.redirect_to;
      if (/^\/api(\/|$)/.test(url)) url = url.replace(/^\/api/, '');
      const csvRes = await AuthenticatedHttp.get(url, { responseType: 'blob' });
      const blob = new Blob([csvRes.data]);
      const fileExt = url.includes('/csv') ? 'csv' : extension || 'csv';
      downloadFile({
        url: window.URL.createObjectURL(blob),
        filename: `orders_report.${fileExt}`,
      });
      return { emailed: false };
    }

    // Sem `job_id` nem `redirect_to` é o zero do gate: o back responde 200 só com o `detail`
    // quando o filtro não tem venda, e nenhum arquivo nem e-mail sai.
    return { emailed: false, empty: true };
  }

  const blob = new Blob([res.data]);
  downloadFile({
    url: window.URL.createObjectURL(blob),
    filename: `orders_report.${extension}`,
  });
  return { emailed: false };
};

export const resendOrderEmailService = async (orderId) =>
  AuthenticatedHttp.post(`/order/${orderId}/resend_approved_email/`).then(({ data }) => data);

export const getOrderApprovalEmailService = async (orderId) =>
  AuthenticatedHttp.get(`/order/${orderId}/approval_email/`).then(({ data }) => data);

export const getRefundReceiptService = async (orderId) =>
  AuthenticatedHttp.get(`/order/${orderId}/refund_receipt/`).then(({ data }) => data);
